/**
 * Loyalty Preview Store
 *
 * A custom Redux store that manages the preview state for loyalty blocks.
 * This allows all loyalty blocks to subscribe to a single global preview state,
 * enabling editors to see how blocks render for different user types.
 */
import { createReduxStore, register } from '@wordpress/data';
import type { StoreDescriptor } from '@wordpress/data/build-types/types';

// ============================================================================
// Types
// ============================================================================

/**
 * Preview state representing different user types
 */
export type PreviewState = 'guest' | 'loggedInNotEnrolled' | 'enrolled';

/**
 * State shape for the loyalty preview store
 */
interface LoyaltyPreviewState {
	readonly previewAs: PreviewState;
}

/**
 * Action types
 */
type SetPreviewStateAction = {
	type: 'SET_PREVIEW_STATE';
	previewAs: PreviewState;
};

type LoyaltyPreviewAction = SetPreviewStateAction;

/**
 * Action creators
 */
interface LoyaltyPreviewActions {
	setPreviewState(previewAs: PreviewState): SetPreviewStateAction;
}

/**
 * Selectors
 */
interface LoyaltyPreviewSelectors {
	getPreviewState(state: LoyaltyPreviewState): PreviewState;
}

/**
 * Complete store configuration
 */
export interface LoyaltyPreviewStoreConfig {
	state: LoyaltyPreviewState;
	actions: LoyaltyPreviewActions;
	selectors: LoyaltyPreviewSelectors;
}

// ============================================================================
// Implementation
// ============================================================================

const DEFAULT_STATE: LoyaltyPreviewState = {
	previewAs: 'guest',
};

const actions: LoyaltyPreviewActions = {
	setPreviewState(previewAs: PreviewState): SetPreviewStateAction {
		return {
			type: 'SET_PREVIEW_STATE',
			previewAs,
		};
	},
};

const selectors: LoyaltyPreviewSelectors = {
	getPreviewState(state: LoyaltyPreviewState): PreviewState {
		return state.previewAs;
	},
};

function reducer(
	state = DEFAULT_STATE,
	action: LoyaltyPreviewAction
): LoyaltyPreviewState {
	switch (action.type) {
		case 'SET_PREVIEW_STATE':
			return {
				...state,
				previewAs: action.previewAs,
			};
		default:
			return state;
	}
}

// ============================================================================
// Store Registration
// ============================================================================

export const LOYALTY_PREVIEW_STORE_NAME = 'leat/loyalty-preview' as const;

const storeConfig = {
	reducer,
	actions,
	selectors,
};

export const loyaltyPreviewStore: StoreDescriptor<
	typeof storeConfig
> = createReduxStore(LOYALTY_PREVIEW_STORE_NAME, storeConfig);

register(loyaltyPreviewStore);
